import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, tokenStore } from "./api";
import { installPushListener } from "./push";

const AuthCtx = createContext(null);

// Extracts the bearer-fallback token from a login/accept-invite response,
// persists it, and returns a clean user object (token stripped).
function captureToken(data) {
  if (!data) return data;
  if (data.access_token) tokenStore.set(data.access_token);
  const { access_token: _a, refresh_token: _r, ...rest } = data;
  return rest;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=unauth, obj=auth
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    installPushListener();
    let cancelled = false;
    api
      .get("/auth/me")
      .then((r) => { if (!cancelled) setUser(r.data); })
      .catch(() => { if (!cancelled) setUser(false); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    const u = captureToken(data);
    setUser(u);
    return u;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    tokenStore.clear();
    setUser(false);
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, logout, refresh, setUser, captureToken }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
